/* Quantity intents survive section replacement and Shopify discount line-key changes. */
(function (root) {
  function identity(item) {
    const properties = Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify([Number(item.variant_id), properties, item.selling_plan_allocation?.selling_plan?.id || item.selling_plan_id || null]);
  }

  function totals(items) {
    const result = new Map();
    for (const item of items) result.set(identity(item), (result.get(identity(item)) || 0) + item.quantity);
    return result;
  }

  function operation(items, desired, protectionId) {
    const current = totals(items);
    // Increases must use change.js: update.js does not enforce existing-line stock.
    for (const [group, quantity] of desired) {
      if (quantity > (current.get(group) || 0)) {
        const item = items.find(item => identity(item) === group);
        if (!item) throw new Error('The cart changed. Please try again.');
        return { url: '/cart/change.js', group, body: { id: item.key, quantity: item.quantity + quantity - current.get(group) } };
      }
    }
    const updates = {};
    let realQuantity = 0;
    const remaining = new Map(desired);
    for (const item of items) {
      const group = identity(item);
      const quantity = remaining.has(group) ? Math.min(item.quantity, remaining.get(group)) : item.quantity;
      if (remaining.has(group)) remaining.set(group, remaining.get(group) - quantity);
      if (quantity !== item.quantity) updates[item.key] = quantity;
      if (Number(item.variant_id) !== Number(protectionId)) realQuantity += quantity;
    }
    // Remove the final products AND protection in one server transaction.
    if (!realQuantity) {
      for (const item of items) if (Number(item.variant_id) === Number(protectionId)) updates[item.key] = 0;
    }
    return Object.keys(updates).length ? { url: '/cart/update.js', body: { updates } } : null;
  }

  class QuantityQueue {
    constructor({ items, protectionId, send, finish, fail, delay = 100 }) {
      this.items = items;
      this.protectionId = protectionId;
      this.send = send;
      this.finish = finish;
      this.fail = fail;
      this.delay = delay;
      this.desired = new Map();
      this.revisions = new Map();
      this.running = false;
    }

    change(item, delta) {
      const group = identity(item);
      const previous = this.desired.has(group) ? this.desired.get(group) : (totals(this.items).get(group) || 0);
      this.desired.set(group, Math.max(0, previous + delta));
      this.revisions.set(group, (this.revisions.get(group) || 0) + 1);
      clearTimeout(this.timer);
      if (!this.running) this.timer = setTimeout(() => this.flush(), this.delay);
    }

    async flush() {
      if (this.running) return;
      this.running = true;
      let response;
      try {
        while (this.desired.size) {
          const request = operation(this.items, this.desired, this.protectionId);
          if (!request) { this.desired.clear(); break; }
          const sent = new Map(this.desired);
          const revisions = new Map(this.revisions);
          response = await this.send(request.url, request.body);
          if (!Array.isArray(response.items)) throw new Error('Unable to confirm the cart update.');
          this.items = response.items;
          const actual = totals(this.items);
          if (request.group && (actual.get(request.group) || 0) !== sent.get(request.group)) {
            throw new Error('The requested quantity is not available.');
          }
          for (const [group, wanted] of sent) {
            if (this.revisions.get(group) !== revisions.get(group)) continue;
            if ((actual.get(group) || 0) === wanted) this.desired.delete(group);
          }
          if (request.url === '/cart/update.js' && [...sent].some(([group, wanted]) => (actual.get(group) || 0) !== wanted)) {
            throw new Error('Unable to confirm the requested quantities.');
          }
        }
        this.finish(response);
      } catch (error) {
        this.desired.clear();
        await this.fail(error);
      } finally {
        this.running = false;
      }
    }
  }
  root.BJCartQuantity = { Queue: QuantityQueue, identity, operation };
})(typeof window === 'undefined' ? globalThis : window);
